import type { JsonFormsData } from '@restore/reform';
import { baseApiUrl } from 'src/config';
import { getAccessToken, keycloak } from 'src/routes/AuthContext';

export const autosave = async (
  realm: string,
  formId: string,
  uuid: string,
  formData: JsonFormsData
): Promise<boolean> => {
  const body = {
    webhookUuid: uuid,
    submissionResponses: formData,
  };

  const token = await getAccessToken();
  return fetch(`${baseApiUrl}/document/form/autosave/post/${formId}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-tenant-id': realm,
    },
    body: JSON.stringify(body),
  })
    .then((response) => {
      if (response.status === 200) {
        return true;
      }

      return false;
    })
    .catch((error) => {
      throw Error(`Error fetching form fields: ${error}`);
    });
};

/**
 * Fire-and-forget autosave for page-hide / unload, dispatched by Reform's
 * `unloadSaveCallback`. This must run fully synchronously — the page may be torn
 * down before any async work resolves — so it reads the current Keycloak token
 * directly (no async refresh) and uses `fetch` with `keepalive: true` so the
 * request survives teardown. Returns whether the request was *dispatched*; the
 * server response can't be observed at unload. Reform guards the payload size
 * before calling this, so we don't re-check the keepalive 64KB limit here.
 */
export const autosaveKeepalive = (
  realm: string,
  formId: string,
  uuid: string,
  formData: JsonFormsData
): boolean => {
  const token = keycloak.token;
  if (!token) {
    return false;
  }

  const body = {
    webhookUuid: uuid,
    submissionResponses: formData,
  };

  try {
    fetch(`${baseApiUrl}/document/form/autosave/post/${formId}`, {
      method: 'POST',
      keepalive: true,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-tenant-id': realm,
      },
      body: JSON.stringify(body),
    }).catch(() => {
      // Fire-and-forget: the page is likely gone before this resolves.
    });
    return true;
  } catch {
    return false;
  }
};
